import { Tabs } from '@material-tailwind/react';
import classNames from 'classnames';
import { createValidityState } from 'hooks/forms';
import { ComponentProps, JSX, useEffect, useRef, useState } from 'react';

type Props = Omit<ComponentProps<typeof Tabs>, 'error'> & {
  disabled?: boolean;
  onValidate?: (validity: { valid: boolean }) => void | string;
  required?: boolean;
  options: {
    value: string;
    label: string;
  }[];
};

export function AlphaTabs(props: Props): JSX.Element {
  const {
    onValidate,
    required = false,
    options,
    disabled = false,
    ...passThrough
  } = props;

  type Value = string | number | readonly string[] | undefined;
  const [state, setState] = useState<Value>(
    () => props.value ?? props.defaultValue,
  );
  const [interaction, setInteraction] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validity = createValidityState({
    valueMissing: Boolean(required && !state),
  });

  const errorMessage = onValidate?.(validity) ?? '';

  useEffect(() => {
    inputRef.current?.setCustomValidity(errorMessage);
  }, [errorMessage]);

  return (
    <div>
      <input
        disabled={disabled}
        type="text"
        hidden
        required
        name={props.name}
        value={state ?? ''}
        onInvalid={() => setInteraction(true)}
        // A change event handler is required to suppress a React warning, but not strictly required
        onChange={(event) => setState(event.currentTarget.value)}
      />
      <div
        role="tablist"
        tabIndex={-1}
        className={classNames(
          'flex h-button/size w-full items-center overflow-hidden rounded-lg transition-all duration-200 md:rounded-xl',
          {
            'border border-highlights/red-dark': interaction && errorMessage,
            'hover:shadow-md': !disabled,
          },
        )}
        {...passThrough}
      >
        {options.map((option, index) => {
          const selected = option.value === state;
          return (
            <div
              key={option.value}
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className={classNames(
                'flex h-full flex-1 grow items-center p-0.5 bg-gray-200 lg:p-1',
                index === 0 && 'rounded-l-lg md:rounded-l-xl',
                index === options.length - 1 && 'rounded-r-lg md:rounded-r-xl',
              )}
            >
              <div
                data-testid={props.name}
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className={classNames(
                  'flex size-full select-none items-center justify-center text-nowrap rounded-lg p-1 text-center transition-all duration-200 alpha-form-input md:rounded-xl md:p-1.5',
                  selected && 'shadow-2 bg-white',
                  selected && !disabled && 'text-text/primary',
                  selected && disabled && 'text-[--colors-neutral-gray-500]',
                  !selected &&
                    !disabled &&
                    'text-gray-600 hover:text-text/primary',
                  !selected && disabled && 'text-[--colors-neutral-gray-500]',
                  disabled && 'cursor-not-allowed',
                )}
                {...(!disabled && {
                  onClick: () => {
                    if (state === option.value) return;
                    setState(option.value);
                    setInteraction(true);
                    props.onChange?.(option.value);
                  },
                  role: 'button',
                  tabIndex: 0,
                })}
              >
                {option.label}
              </div>
            </div>
          );
        })}
      </div>
      {interaction && errorMessage && (
        <div className="px-padding/x-sm pt-1 text-highlights/red-dark alpha-caption">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
